import { Controller } from '@hotwired/stimulus'

// Toggles between the domestic (US) and global program offer forms on the
// unified "New Program Offer" page (CTS-10091), based on the single Country
// selector (sentinel value "US" = domestic; anything else = global). For a
// global selection, also syncs the hidden `program_country` select inside the
// global form partial and dispatches a change event so the existing
// hr-portal-global-program-select Stimulus controller repopulates program
// level/duration/outreach-eligible exactly as it does on the standalone
// Global Program Offer page.
const DOMESTIC_COUNTRY_CODE = 'US'
const HYBRID_COUNTRY_CODES = ['CA', 'BB', 'JM', 'PR', 'TT', 'VI']
const CITY_REQUIRED_COUNTRY_CODES = ['CA']

export default class extends Controller {
  static targets = [
    'countrySelect',
    'domesticPanel',
    'globalPanel',
    'globalCountryField',
    'hybridCityField',
  ]

  static values = {
    programsMap: Object,
  }

  connect() {
    this.updatePanels()
  }

  updatePanels() {
    const country = this.countrySelectTarget.value
    const isDomestic =
      country === DOMESTIC_COUNTRY_CODE ||
      (HYBRID_COUNTRY_CODES.includes(country) &&
        this.hybridCountryHasDomesticDelivery(country))

    this.domesticPanelTarget.classList.toggle('hidden', !isDomestic)
    this.globalPanelTarget.classList.toggle('hidden', isDomestic)

    if (isDomestic) {
      this.updateDomesticLocale(country)
      this.updateHybridCityField(country)
    }

    if (!isDomestic && this.hasGlobalCountryFieldTarget) {
      this.globalCountryFieldTarget.value = country
      this.globalCountryFieldTarget.dispatchEvent(
        new Event('change', { bubbles: true }),
      )
    }
  }

  // A hybrid country (see Billing::ContractProgram::HYBRID_DELIVERY_COUNTRIES)
  // can have its program row(s) configured for either domestic (YNS direct,
  // fulfilled via this page's Invitation panel) or global (CSG partner,
  // fulfilled via the Global Referral panel) delivery. Legacy
  // GlobalContractLicenseDetail-sourced entries have no `delivery_mode` key at
  // all (that split predates them) and were always domestic-fulfilled, so
  // their absence is treated as domestic for backward compatibility. If the
  // country has no catalog entries at all (e.g. programsMapValue wasn't
  // populated), default to domestic too, preserving this page's original
  // behavior rather than silently hiding the form.
  hybridCountryHasDomesticDelivery(country) {
    const levels = this.programsMapValue?.[country]?.levels
    if (!levels) return true

    const durations = Object.values(levels).flat()
    if (durations.length === 0) return true

    return durations.some((duration) => duration.delivery_mode !== 'global')
  }

  updateHybridCityField(country) {
    if (!this.hasHybridCityFieldTarget) return

    const required = CITY_REQUIRED_COUNTRY_CODES.includes(country)
    this.hybridCityFieldTarget.classList.toggle('hidden', !required)
    const cityField = this.hybridCityFieldTarget.querySelector('input')
    if (cityField) cityField.required = required
  }

  updateDomesticLocale(country) {
    const localeField =
      this.domesticPanelTarget.querySelector('[name$="[locale]"]')
    if (!localeField) return

    const suffix =
      country === DOMESTIC_COUNTRY_CODE ? '_us' : `_${country.toLowerCase()}`
    if (localeField.tagName !== 'SELECT') {
      localeField.value =
        country === DOMESTIC_COUNTRY_CODE ? 'en' : `en_${country.toLowerCase()}`
      this.dispatchLocaleChange(localeField)
      return
    }

    const options = Array.from(localeField.options)
    options.forEach((option) => {
      if (!option.value) return
      const visible =
        country === DOMESTIC_COUNTRY_CODE
          ? option.value === 'en' || option.value.endsWith(suffix)
          : HYBRID_COUNTRY_CODES.includes(country) &&
            option.value.endsWith(suffix)
      option.hidden = !visible
    })

    const selected = options.find((option) => !option.hidden && option.value)
    if (selected) localeField.value = selected.value
    this.dispatchLocaleChange(localeField)
  }

  // The locale field drives the Program Level/Duration selects via
  // hr-portal-invitation#updateProgramLevelAndDurations, which only runs on
  // the locale field's native 'change' event -- setting .value directly (as
  // above) doesn't fire one, so it must be dispatched explicitly whenever the
  // Country selector changes the locale out from under the invitation form.
  dispatchLocaleChange(localeField) {
    localeField.dispatchEvent(new Event('change', { bubbles: true }))
  }
}
